import { useState, useEffect, useCallback } from 'react';
import TT from '../../src/Component/assets/logo/TT.svg';
import EVO from '../../src/Component/assets/logo/EVO.svg';
import ONTIME from '../../src/Component/assets/logo/ONTIME.svg';
import ZIPPY from '../../src/Component/assets/logo/ZIPPY.svg';
import SBAELD from '../../src/Component/assets/logo/sba.svg';
import RTELOGS from '../../src/Component/assets/logo/rtelogs.svg';
import RULER from '../../src/Component/assets/logo/RULER.svg';
import DELTA from '../../src/Component/assets/logo/DELTA.svg';

import TT_LOGIN from '../../src/Component/assets/logo/TT_LOGIN.svg';
import EVO_LOGIN from '../../src/Component/assets/logo/EVO_LOGIN.svg';
import ONTIME_LOGIN from '../../src/Component/assets/logo/ONTIME_LOGIN.svg';
import ZIPPY_LOGIN from '../../src/Component/assets/logo/ZIPPY_LOGIN.svg';
import SBAELD_LOGIN from '../../src/Component/assets/logo/SBA_LOGIN.svg';
import RTE_LOGIN from '../../src/Component/assets/logo/RTE_LOGIN.svg';
import RULER_LOGIN from '../../src/Component/assets/logo/Ruler_Login.svg';
import DELTA_LOGIN from '../../src/Component/assets/logo/DELTA_LOGIN.svg';
import FLEETVISION from '../../src/Component/assets/logo/FLEETVISION.svg';

export enum EProjectNames {
  TTELD = 'tteld',
  EVO = 'evoeld',
  ONTIME = 'ontime-logs',
  ZIPPY = 'zippyeld',
  UATELD = 'uateld',
  BTEST = 'btest',
  FBUAT = 'fbuat',
  SBAELD = 'sbaeld',
  RTE = 'rtelogs',
  RULER = 'rulereld',
  FLEETVISION = 'fleetvision',
  RRELD = 'rreld',
  PROTRUX = 'protrux',
  FIVEELD = 'fiveeld',
  DELTAELD = 'deltaeld',
}

// Projects that get RTE Logs' extra access (Unpaid Invoices, Free Trial tab, DashCam Contract, etc.)
export const RTE_ACCESS_PROJECTS: string[] = [EProjectNames.RTE, EProjectNames.DELTAELD];

export const hasRteAccess = (projectName?: string): boolean =>
  !!projectName && RTE_ACCESS_PROJECTS.includes(projectName);

// Internal test environments, where some admin restrictions are relaxed for QA
export const TEST_ENV_PROJECTS: string[] = [EProjectNames.UATELD, EProjectNames.BTEST];

export const isTestEnvProject = (projectName?: string): boolean =>
  !!projectName && TEST_ENV_PROJECTS.includes(projectName);

// No brand assets exist yet for RRELD — a plain placeholder mark until real branding is provided.
const RRELD_LOGO = `data:image/svg+xml;utf8,${encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40"><circle cx="20" cy="20" r="20" fill="#6B7280"/><text x="50%" y="56%" font-size="18" fill="#fff" text-anchor="middle" font-family="sans-serif">R</text></svg>`,
)}`;

// No brand assets exist yet for ProTRUX — a plain placeholder mark until real branding is provided.
const PROTRUX_LOGO = `data:image/svg+xml;utf8,${encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40"><circle cx="20" cy="20" r="20" fill="#2563EB"/><text x="50%" y="56%" font-size="18" fill="#fff" text-anchor="middle" font-family="sans-serif">P</text></svg>`,
)}`;

// No brand assets exist yet for Five ELD — a plain placeholder mark until real branding is provided.
const FIVEELD_LOGO = `data:image/svg+xml;utf8,${encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40"><circle cx="20" cy="20" r="20" fill="#059669"/><text x="50%" y="56%" font-size="18" fill="#fff" text-anchor="middle" font-family="sans-serif">5</text></svg>`,
)}`;

interface ProjectDataType {
  projectName: EProjectNames;
  title: string;
  logo: string;
  loginLogo?: string;
  baseUrl: string;
  socketUrl: string;
  color?: {
    headerBackground: string;
    headerTitleColor: string;
  };
  favicon: string;
  reportUrl: string;
}

export const PROJECT_DATA: Record<EProjectNames, ProjectDataType> = {
  [EProjectNames.TTELD]: {
    projectName: EProjectNames.TTELD,
    title: 'TT ELD',
    logo: TT,
    baseUrl: 'https://addmin-api.tteld.com/api/',
    socketUrl: 'https://addmin-api.tteld.com',
    color: {
      headerBackground: '#FFBB00',
      headerTitleColor: '#1f1604',
    },
    loginLogo: TT_LOGIN,
    favicon: '/favicons/tteld.svg',
    reportUrl: 'https://report.tteld.com',
  },
  [EProjectNames.EVO]: {
    projectName: EProjectNames.EVO,
    title: 'EVO ELD',
    logo: EVO,
    baseUrl: 'https://addmin-api.evoeld.com/api/',
    socketUrl: 'https://addmin-api.evoeld.com',
    loginLogo: EVO_LOGIN,
    color: {
      headerBackground: '#06B4EA',
      headerTitleColor: '#ffffff',
    },
    favicon: '/favicons/evoeld.svg',
    reportUrl: 'https://report.evoeld.com',
  },
  [EProjectNames.ONTIME]: {
    projectName: EProjectNames.ONTIME,
    title: 'Ontime-Logs',
    logo: ONTIME,
    baseUrl: 'https://addmin-api.ontime-logs.com/api/',
    socketUrl: 'https://addmin-api.ontime-logs.com',
    loginLogo: ONTIME_LOGIN,
    color: {
      headerBackground: '#F64847',
      headerTitleColor: '#ffffff',
    },
    favicon: '/favicons/ontime.svg',
    reportUrl: 'https://report.ontime-logs.com',
  },
  [EProjectNames.ZIPPY]: {
    projectName: EProjectNames.ZIPPY,
    title: 'Zippy ELD',
    logo: ZIPPY,
    baseUrl: 'https://addmin-api.zippyeld.com/api/',
    socketUrl: 'https://addmin-api.zippyeld.com',
    loginLogo: ZIPPY_LOGIN,
    color: {
      headerBackground: '#0D63E4',
      headerTitleColor: '#ffffff',
    },
    favicon: '/favicons/zippy.svg',
    reportUrl: 'https://report.zippyeld.com',
  },
  [EProjectNames.SBAELD]: {
    projectName: EProjectNames.SBAELD,
    title: 'SBA ELD',
    logo: SBAELD,
    baseUrl: 'https://front-api.sbaeld.com/api/',
    socketUrl: 'https://front-api.sbaeld.com',
    color: {
      headerBackground: '#48B5C5',
      headerTitleColor: '#fff',
    },
    loginLogo: SBAELD_LOGIN,
    favicon: '/favicons/sbaeld.svg',
    reportUrl: 'https://report.sbaeld.com',
  },
  [EProjectNames.RTE]: {
    projectName: EProjectNames.RTE,
    title: 'RTE Logs',
    logo: RTELOGS,
    baseUrl: 'https://front-api.rtelogs.com/api/',
    socketUrl: 'https://front-api.rtelogs.com',
    color: {
      headerBackground: '#1A3E6A',
      headerTitleColor: '#fff',
    },
    loginLogo: RTE_LOGIN,
    favicon: '/favicons/rtelogs.svg',
    reportUrl: 'https://report.rtelogs.com',
  },
  [EProjectNames.RULER]: {
    projectName: EProjectNames.RULER,
    title: 'Ruler ELD',
    logo: RULER,
    baseUrl: 'https://front-api.rulereld.com/api/',
    socketUrl: 'https://front-api.rulereld.com',
    color: {
      headerBackground: '#FF9503',
      headerTitleColor: '#1f1604',
    },
    loginLogo: RULER_LOGIN,
    favicon: '/favicons/ruler.svg',
    reportUrl: 'https://report.rulereld.com',
  },
  [EProjectNames.UATELD]: {
    projectName: EProjectNames.UATELD,
    title: 'UAT ELD',
    logo: TT,
    baseUrl: 'https://uat.tteld.com/api/',
    socketUrl: 'https://uat.tteld.com',
    color: {
      headerBackground: '#FFBB00',
      headerTitleColor: '#1f1604',
    },
    loginLogo: TT_LOGIN,
    favicon: '/favicons/tteld.svg',
    reportUrl: 'https://uat.tteld.com',
  },
  [EProjectNames.BTEST]: {
    projectName: EProjectNames.BTEST,
    title: 'BTEST ELD',
    logo: TT,
    baseUrl: 'https://btest.tteld.com/api/',
    socketUrl: 'https://btest.tteld.com',
    color: {
      headerBackground: '#978e76',
      headerTitleColor: '#241a06',
    },
    loginLogo: TT_LOGIN,
    favicon: '/favicons/tteld.svg',
    reportUrl: 'https://btest.tteld.com',
  },
  [EProjectNames.FBUAT]: {
    projectName: EProjectNames.FBUAT,
    title: 'FB UAT',
    logo: TT,
    baseUrl: 'https://fbuat.tteld.com/api/',
    socketUrl: 'https://fbuat.tteld.com',
    color: {
      headerBackground: '#FFBB00',
      headerTitleColor: '#1f1604',
    },
    loginLogo: TT_LOGIN,
    favicon: '/favicons/tteld.svg',
    reportUrl: 'https://fbuat.tteld.com',
  },
  [EProjectNames.RRELD]: {
    projectName: EProjectNames.RRELD,
    title: 'RRELD',
    logo: RRELD_LOGO,
    baseUrl: 'https://addmin-api.rreld.com/api/',
    socketUrl: 'https://addmin-api.rreld.com',
    color: {
      headerBackground: '#6B7280',
      headerTitleColor: '#ffffff',
    },
    loginLogo: RRELD_LOGO,
    favicon: '/favicon.ico',
    reportUrl: 'https://report.rreld.com',
  },
  [EProjectNames.PROTRUX]: {
    projectName: EProjectNames.PROTRUX,
    title: 'ProTRUX',
    logo: PROTRUX_LOGO,
    baseUrl: 'https://addmin-api.protrux.com/api/',
    socketUrl: 'https://addmin-api.protrux.com',
    color: {
      headerBackground: '#2563EB',
      headerTitleColor: '#ffffff',
    },
    loginLogo: PROTRUX_LOGO,
    favicon: '/favicon.ico',
    reportUrl: 'https://report.protrux.com',
  },
  [EProjectNames.FIVEELD]: {
    projectName: EProjectNames.FIVEELD,
    title: 'Five ELD',
    logo: FIVEELD_LOGO,
    baseUrl: 'https://addmin-api.fiveeld.com/api/',
    socketUrl: 'https://addmin-api.fiveeld.com',
    color: {
      headerBackground: '#059669',
      headerTitleColor: '#ffffff',
    },
    loginLogo: FIVEELD_LOGO,
    favicon: '/favicon.ico',
    reportUrl: 'https://report.fiveeld.com',
  },
  [EProjectNames.DELTAELD]: {
    projectName: EProjectNames.DELTAELD,
    title: 'Delta ELD',
    logo: DELTA,
    baseUrl: 'https://addmin-api.delta-eld.com/api/',
    socketUrl: 'https://addmin-api.delta-eld.com',
    color: {
      headerBackground: '#D12026',
      headerTitleColor: '#fff',
    },
    loginLogo: DELTA_LOGIN,
    favicon: '/favicons/deltaeld.png',
    reportUrl: 'https://read.delta-eld.com',
  },
  [EProjectNames.FLEETVISION]: {
    projectName: EProjectNames.FLEETVISION,
    title: 'Fleet Vision',
    logo: FLEETVISION,
    baseUrl: 'https://addmin-api.tteld.com/api/',
    socketUrl: 'https://addmin-api.tteld.com',
    color: {
      headerBackground: '#5263f7',
      headerTitleColor: '#ffffff',
    },
    loginLogo: FLEETVISION,
    favicon: '/favicons/tteld.svg',
    reportUrl: 'https://report.fleetvision.co',
  },
};

export function detectProject(): EProjectNames {
  const hostname = window.location.hostname.toLowerCase();

  if (hostname.includes('fbuat')) return EProjectNames.FBUAT;
  if (hostname.includes('uat')) return EProjectNames.UATELD;
  if (hostname.includes('btest')) return EProjectNames.BTEST;
  if (hostname.includes('delta-eld')) return EProjectNames.DELTAELD;
  if (hostname.includes('tteld')) return EProjectNames.TTELD;
  if (hostname.includes('evo')) return EProjectNames.EVO;
  if (hostname.includes('ontime')) return EProjectNames.ONTIME;
  if (hostname.includes('zippy')) return EProjectNames.ZIPPY;
  if (hostname.includes('sbaeld')) return EProjectNames.SBAELD;
  if (hostname.includes('rte')) return EProjectNames.RTE;
  if (hostname.includes('ruler')) return EProjectNames.RULER;
  if (hostname.includes('fleetvision')) return EProjectNames.FLEETVISION;
  if (hostname.includes('rreld')) return EProjectNames.RRELD;
  if (hostname.includes('protrux')) return EProjectNames.PROTRUX;
  if (hostname.includes('fiveeld')) return EProjectNames.FIVEELD;

  // development
  return EProjectNames.UATELD;
}

const SELECTED_PLATFORM_KEY = 'selectedPlatform';

export function resolveActiveProject(): EProjectNames {
  const stored = localStorage.getItem(SELECTED_PLATFORM_KEY) as EProjectNames | null;
  if (stored && PROJECT_DATA[stored]) return stored;
  return detectProject();
}

export function useProjectData() {
  const [project, setProject] = useState<EProjectNames>(resolveActiveProject());
  const [data, setData] = useState<ProjectDataType>(PROJECT_DATA[project]);

  useEffect(() => {
    setData(PROJECT_DATA[project]);
  }, [project]);

  const updateProject = useCallback((newProject: EProjectNames) => {
    if (PROJECT_DATA[newProject]) {
      setProject(newProject);
    }
  }, []);

  return {
    project,
    projectData: data,
    setProject: updateProject,
  };
}
